"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UrlParser = void 0;
/**
 * URL解析工具类
 */
class UrlParser {
    /**
     * 创建UrlParser实例
     * @param url 完整的URL字符串
     */
    constructor(url) {
        this.urlObj = new URL(url);
    }
    /**
     * 将URL链接中的参数解析为一个对象返回
     * @returns {object} query参数对象
     */
    query() {
        const searchParams = this.urlObj.searchParams;
        const queryObj = {};
        for (const [key, value] of searchParams) {
            queryObj[key] = value;
        }
        return queryObj;
    }
    /**
     * 同location中的hostname
     * @returns {string} 主机名
     */
    get hostname() {
        return this.urlObj.hostname;
    }
    /**
     * 同location中的origin
     * @returns {string} 源
     */
    get origin() {
        return this.urlObj.origin;
    }
    /**
     * 同location中的pathname
     * @returns {string} 路径名
     */
    get pathname() {
        return this.urlObj.pathname;
    }
    /**
     * 将参数对象添加到URL链接上并返回拼接好的URL
     * 支持 emoji、中文等 Unicode 字符和特殊字符的正确编码
     * @param params 参数对象
     * @param needExistParams 是否需要保留原有的参数，默认保留
     * @returns {string} 拼接好的URL
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getUrlByParams(params, needExistParams = true) {
        // 创建URL副本，避免修改原始对象
        const newUrl = new URL(this.urlObj.toString());
        // 合并参数，优先使用params中的值覆盖URL中的参数
        const existingParams = needExistParams ? this.query() : {};
        const mergedParams = Object.assign(Object.assign({}, existingParams), params);
        // 分别处理基本类型和对象类型参数
        const { primitiveParams, objectParams } = this.separateParams(mergedParams);
        // 构建查询字符串
        const queryString = this.buildQueryString(primitiveParams, objectParams);
        // 更新URL的搜索参数
        newUrl.search = queryString;
        return newUrl.toString();
    }
    /**
     * 分离基本类型和对象类型参数
     * @param params 参数对象
     * @returns 分离后的参数
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    separateParams(params) {
        const primitiveParams = {};
        const objectParams = [];
        Object.entries(params).forEach(([key, value]) => {
            // 跳过 null 和 undefined 值
            if (value === null || value === undefined) {
                return;
            }
            // 处理对象和数组类型
            if (typeof value === 'object') {
                try {
                    // JSON.stringify 会保留 Unicode 字符（包括 emoji、中文等）
                    const jsonString = JSON.stringify(value);
                    // encodeURIComponent 会将 Unicode 字符编码为 UTF-8 百分比编码
                    const encodedValue = encodeURIComponent(jsonString);
                    objectParams.push([key, encodedValue]);
                }
                catch (_a) {
                    // 序列化失败（如循环引用），跳过该参数
                    console.error('序列化失败', key, value);
                    return;
                }
            }
            else {
                // 基本类型（字符串、数字、布尔值）
                // URLSearchParams 会自动处理 Unicode 字符（包括 emoji、中文等）的编码
                primitiveParams[key] = String(value);
            }
        });
        return { primitiveParams, objectParams };
    }
    /**
     * 构建查询字符串
     * @param primitiveParams 基本类型参数
     * @param objectParams 对象类型参数（已编码的键值对）
     * @returns 查询字符串
     */
    buildQueryString(primitiveParams, objectParams) {
        // 使用 URLSearchParams 处理基本类型参数（自动编码）
        const searchParams = new URLSearchParams();
        Object.entries(primitiveParams).forEach(([key, value]) => {
            searchParams.set(key, value);
        });
        const primitiveQuery = searchParams.toString();
        // 手动构建对象类型参数的查询字符串（避免双重编码）
        // 注意：键也需要编码，因为：
        // 1. 键可能包含特殊字符（如 &, =），不编码会导致URL解析错误
        // 2. 键可能包含 emoji、中文等 Unicode 字符，需要编码为 UTF-8 百分比编码
        // 3. 与 URLSearchParams 的行为保持一致（URLSearchParams 会自动编码键）
        const objectQueryParts = objectParams.map(([key, encodedValue]) => {
            const encodedKey = encodeURIComponent(key);
            return `${encodedKey}=${encodedValue}`;
        });
        const objectQuery = objectQueryParts.join('&');
        // 合并两部分查询字符串
        if (primitiveQuery && objectQuery) {
            return `${primitiveQuery}&${objectQuery}`;
        }
        return primitiveQuery || objectQuery;
    }
}
exports.UrlParser = UrlParser;
//# sourceMappingURL=data:application/json;base64,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